import React, { useCallback, useMemo, useState } from 'react';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { setNextShowForPopupSignUpDelay } from 'helpers/setNextShowForPopupSignUpDelay';
import PropTypes from 'prop-types';

import {
  AccountManager,
  getAvailablePages,
  isUserNeedKyc,
  isUserNeedPartialVerification,
  isUserUnderVerification,
  NotificationManager,
  SettingsManager,
  SiteConfigManager,
  useApplicationState,
  useSettings,
  useUserMessages,
} from '@atlas/core';
import {
  availableClientProfileRoutes,
  CLIENT_PROFILE_COMPONENT_TYPES,
  clientProfileRoutes,
  COMPONENT_TYPES,
  staticRoutes,
} from '@atlas/routing';
import { Dropdown, Icon, Label } from '@atlas/ui';

import { ACCOUNT_STATUSES } from 'common/constants';

import HeaderInfoAuthorizedBalance from './HeaderInfoAuthorizedBalance';
import SmarticoInboxButton from './SmarticoInboxButton';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;

const availableRoutes = Object.assign({}, availableClientProfileRoutes);
delete availableRoutes[CLIENT_PROFILE_COMPONENT_TYPES.SETTINGS];

const HeaderInfoAuthorized = ({
  accountDropdownTabs,
  depositBtnLink,
  isIgnoredUnreadMessages,
  logoutPopupType,
  logoutRedirectLink,
}) => {
  const { isBalanceHidden, isUsernameHidden } = useSettings();
  const { account } = useApplicationState();
  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const isPartiallyRegistered = account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  const signupLink = showInPopup ? '?cmd=signup' : staticRoutes.signup;
  const depositLink = depositBtnLink || `${clientProfilePath}/${payment}`;

  const linkToDeposit = isPartiallyRegistered ? signupLink : depositLink;

  const [isExpanded, setExpanded] = useState(false);

  return (
    <>
      <SmarticoInboxButton />
      <div className="header-info__bar">
        {!isBalanceHidden && <HeaderInfoAuthorizedBalance />}

        <Link className="header__deposit btn primary medium" to={linkToDeposit}>
          <Label message="deposit" />
        </Link>
      </div>

      <Dropdown
        buttonText={isUsernameHidden ? 'My profile' : account.login}
        className="header__account"
        isActive={isExpanded}
        isBg
        isCloseOnClickInside
        setExpanded={setExpanded}
      >
        <HeaderAccountDropdownContent
          accountDropdownTabs={accountDropdownTabs}
          clientProfilePath={clientProfilePath}
          isBalanceHidden={isBalanceHidden}
          isIgnoredUnreadMessages={isIgnoredUnreadMessages}
          isUsernameHidden={isUsernameHidden}
          logoutPopupType={logoutPopupType}
          logoutRedirectLink={logoutRedirectLink}
        />
      </Dropdown>
    </>
  );
};

HeaderInfoAuthorized.propTypes = {
  accountDropdownTabs: PropTypes.object,
  depositBtnLink: PropTypes.string,
  isIgnoredUnreadMessages: PropTypes.bool,
  logoutPopupType: PropTypes.string,
  logoutRedirectLink: PropTypes.string,
};

const UnreadMessagesNotification = () => {
  const { unreadMessagesCount } = useUserMessages();
  if (unreadMessagesCount > 0) return <span className="dropdown-list-item__notification" />;
  return null;
};

const HeaderAccountDropdownContent = ({
  accountDropdownTabs,
  clientProfilePath,
  isBalanceHidden,
  isIgnoredUnreadMessages,
  isUsernameHidden,
  logoutPopupType,
  logoutRedirectLink,
}) => {
  const history = useHistory();

  const { tabs: clientProfileTabs = [] } = SiteConfigManager.getComponentOptions(CLIENT_PROFILE);

  const toggleHideBalance = useCallback(() => {
    SettingsManager.updateSettings({
      isBalanceHidden: !isBalanceHidden,
    });
  }, [isBalanceHidden]);

  const toggleHideUsername = useCallback(() => {
    SettingsManager.updateSettings({
      isUsernameHidden: !isUsernameHidden,
    });
  }, [isUsernameHidden]);

  const tabs = useMemo(
    () => accountDropdownTabs || getAvailablePages(availableRoutes, clientProfileTabs),
    [accountDropdownTabs, clientProfileTabs],
  );

  const onLogOut = () => {
    NotificationManager.display({
      data: {
        onConfirm: () => {
          const redirectPath =
            logoutRedirectLink ||
            SiteConfigManager.getExtraConfig('components.0.path') ||
            SiteConfigManager.getExtraConfig('components.0.defaultPath', '/');

          setNextShowForPopupSignUpDelay();

          history.push(redirectPath);

          AccountManager.logout();
        },
      },
      message: 'signout_confirm_message',
      title: 'signout_confirm_title',
      type: logoutPopupType || 'confirm',
    });
  };

  const applicationState = useApplicationState();

  const {
    account: { actionsRequiresVerification, verificationUri },
  } = applicationState;
  const needKycNotUnderRegulation =
    !isUserUnderVerification(applicationState?.account) &&
    isUserNeedKyc(applicationState?.account) &&
    !!actionsRequiresVerification;

  const iconsMap = {
    'bonus-icon': 'gift',
    'calendar-icon': 'calendar',
    cashback: 'cashback',
    'messages-link': 'message',
    'promo-code': 'promo-code',
    'security-legitimuz': 'security-legitimuz',
    'self-regulation-icon': 'lock',
    'user-badge-icon': 'account',
    'wallet-icon': 'money',
  };

  return (
    <>
      {tabs.map(({ customLink, icon, link, name, type }) => {
        if (type === 'verification' && !needKycNotUnderRegulation && !verificationUri) {
          return null;
        }

        const isCustomIcon =
          type === 'verification' &&
          (needKycNotUnderRegulation ||
            isUserNeedPartialVerification(applicationState?.account) ||
            isUserUnderVerification);

        return (
          <NavLink
            activeClassName="active"
            className="dropdown-list-item"
            key={type}
            to={customLink || `${clientProfilePath}/${link}`}
          >
            <div className="dropdown-list-item__icon">
              <Icon name={iconsMap[icon]} />
            </div>
            <Label className="account-dropdown__text" message={name} />
            {name === 'Messages' && !isIgnoredUnreadMessages && <UnreadMessagesNotification />}
            {isCustomIcon && <Icon className="error-legitimuz-icon" name="error-legitimuz" />}
          </NavLink>
        );
      })}

      <button
        className="dropdown-list-item hide-info-block"
        onClick={toggleHideBalance}
        type="button"
      >
        <div className="dropdown-list-item__icon">
          <Icon name={isBalanceHidden ? 'visibility-on' : 'visibility-off'} />
        </div>

        <Label
          className="dropdown-list-item__text"
          message={isBalanceHidden ? 'show_balance' : 'hide_balance'}
        />
      </button>

      <button className="dropdown-list-item" onClick={toggleHideUsername} type="button">
        <div className="dropdown-list-item__icon">
          <Icon name={isUsernameHidden ? 'visibility-on' : 'visibility-off'} />
        </div>

        <Label
          className="dropdown-list-item__text"
          message={isUsernameHidden ? 'show_login' : 'hide_login'}
        />
      </button>

      <div className="dropdown-list__footer">
        <button className="dropdown-list-item" onClick={onLogOut} type="button">
          <div className="dropdown-list-item__icon">
            <Icon name="exit" />
          </div>
          <Label className="dropdown-list-item__text" message="logout" />
        </button>
      </div>
    </>
  );
};

HeaderAccountDropdownContent.propTypes = {
  accountDropdownTabs: PropTypes.object,
  clientProfilePath: PropTypes.string,
  isBalanceHidden: PropTypes.bool,
  isIgnoredUnreadMessages: PropTypes.bool,
  isUsernameHidden: PropTypes.bool,
  logoutPopupType: PropTypes.string,
  logoutRedirectLink: PropTypes.string,
};

export default HeaderInfoAuthorized;
